<div class="min-h-screen relative">
    <div class="container mx-auto p-6">
        <div class="fixed top-0 left-0 w-full h-12 bg-green-500 flex items-center z-10">
            <h1 class="text-xl font-bold text-white px-4">Shopping Partner Onboarding</h1>
        </div>
        @if (!utilityService.cameraPermissionStatus?.granted) {
            <div class="flex justify-center mt-12">
                <p-message severity="warn" [size]="'small'">Need camera permission to capture photo
                    and upload.
                    &nbsp;<a style="text-decoration: underline; color:#a16207; cursor: pointer;"
                        (click)="requestCameraPermission()">Grant Permission</a></p-message>
            </div>
        }
        @if(readOnly) {
            <div class="flex justify-center mt-12">
                @if(userProfile?.onboardingStatus === 'under_review') {
                <p-message severity="info" [size]="'small'">Thank you for your interest in becoming a shopper at AnyStore. Your onboarding is under review. Please wait for approval.</p-message>
                } @else if(userProfile?.onboardingStatus === 'rejected') {
                <p-message severity="error" [size]="'small'">Thank you for your interest in becoming a shopper at AnyStore. Your onboarding has been rejected. Please contact support.</p-message>
                }
            </div>
        }
        <form [formGroup]="form" class="mt-12">
            <div class="flex flex-col gap-6 p-6 bg-white rounded-xl">
                <h2 class="text-md font-semibold">Personal Information</h2>
                <p-iftalabel class="flex-1">
                    <input pInputText id="fullName" class="w-full" formControlName="fullName" [readonly]="readOnly" />
                    <label for="fullName">Full Name</label>
                </p-iftalabel>
                <p-iftalabel class="flex-1">
                    <input pInputText id="email" class="w-full" formControlName="email" [readonly]="readOnly" />
                    <label for="email">Email</label>
                </p-iftalabel>
                <p-iftalabel class="flex-1">
                    <input pInputText id="dateOfBirth" class="w-full" formControlName="dateOfBirth" [readonly]="readOnly" />
                    <label for="dateOfBirth">Date of Birth</label>
                </p-iftalabel>
                <p-iftalabel class="flex-1">
                    <input pInputText id="address" class="w-full" formControlName="address" [readonly]="readOnly" />
                    <label for="address">Address</label>
                </p-iftalabel>
            </div>

            <div class="flex flex-col gap-6 p-6 bg-white rounded-xl mt-4">
                <h2 class="text-md font-semibold">Verification Documents</h2>
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-normal text-gray-500">Driver's License</label>
                    @if((userProfile?.onboarding?.driversLicense || []).length > 0) {
                    <div class="flex flex-wrap gap-2 my-2">
                        @for (image of userProfile?.onboarding?.driversLicense || []; track $index) {
                        <div class="relative">
                            <p-image [imageStyle]="{'border-radius': '8px'}"
                                [src]="image.downloadUrl"
                                alt="Image" width="150" [preview]="true" />
                                <!-- add delete icon, position it in the top right corner -->
                                @if(!readOnly) {
                                <i class="pi pi-times absolute top-0 right-0 cursor-pointer text-gray-500 text-xs"
                                    (click)="deleteImage(image, imageCategoryPaths.ShopperDriversLicense)"></i>
                                }
                                </div>
                                }

                    </div>
                    }
                    @if(!readOnly && (userProfile?.onboarding?.driversLicense || []).length <= 2) {
                    <p-fileupload #fileupload class="flex" mode="basic" name="image" chooseIcon="pi pi-upload"
                        [customUpload]="true" [multiple]="true" accept="image/*" capture="environment"
                        [disabled]="readOnly || uploadingImagesInProgress"
                        (uploadHandler)="uploadImage($event, fileupload, imageCategoryPaths.ShopperDriversLicense)"
                        [auto]="true" chooseLabel="Upload" />
                    }
                </div>
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-normal text-gray-500">Selfie with Driver's License</label>
                    @if((userProfile?.onboarding?.selfie || []).length > 0) {
                    <div class="flex flex-wrap gap-2 my-2 relative">
                        @for (image of userProfile?.onboarding?.selfie || []; track $index) {
                        <div class="relative">
                            <p-image [imageStyle]="{'border-radius': '8px'}"
                            [src]="image.downloadUrl" alt="Image"
                            width="150" [preview]="true" />
                            @if(!readOnly) {
                            <i class="pi pi-times absolute top-0 right-0 cursor-pointer text-gray-500 text-xs"
                                (click)="deleteImage(image, imageCategoryPaths.ShopperSelfie)"></i>
                            }
                            </div>
                            }

                    </div>
                    } @else {
                    <p-fileupload #fileupload class="flex" mode="basic" name="image" chooseIcon="pi pi-upload"
                        [customUpload]="true" [multiple]="true" accept="image/*" capture="environment"
                        [disabled]="readOnly || uploadingImagesInProgress"
                        (uploadHandler)="uploadImage($event, fileupload, imageCategoryPaths.ShopperSelfie)"
                        [auto]="true" chooseLabel="Upload" />
                    }

                </div>
            </div>

            <div class="flex flex-col gap-6 p-6 bg-white rounded-xl mt-4">
                <h2 class="text-md font-semibold">Vehicle Information</h2>
                <div class="flex flex-col">
                    <label class="mb-1 font-normal text-sm text-gray-500">Do you own or have access to a 4 wheel
                        vehicle?</label>
                    <div class="flex flex-row items-center space-x-6 mt-1">
                        <div class="flex items-center">
                            <p-radioButton name="hasVehicle" value="yes" inputId="vehicleYes" variant="filled" 
                                formControlName="hasVehicle" [disabled]="readOnly" />
                            <label for="vehicleYes" class="ml-2 text-sm text-gray-700">Yes</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="hasVehicle" value="no" inputId="vehicleNo" variant="filled"
                                formControlName="hasVehicle" [disabled]="readOnly" />
                            <label for="vehicleNo" class="ml-2 text-sm text-gray-700">No</label>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <label class="mb-1 font-normal text-sm text-gray-500">Type of Vehicle</label>
                    <div class="flex flex-row items-center space-x-6 mt-1">
                        <div class="flex items-center">
                            <p-radioButton name="vehicleType" value="car" inputId="vehicleTypeCar" variant="filled"
                                formControlName="vehicleType" [disabled]="readOnly" />
                            <label for="vehicleTypeCar" class="ml-2 text-sm text-gray-700">Car</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="vehicleType" value="suv" inputId="vehicleTypeSuv" variant="filled"
                                formControlName="vehicleType" [disabled]="readOnly" />
                            <label for="vehicleTypeSuv" class="ml-2 text-sm text-gray-700">SUV/Van</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="vehicleType" value="truck" inputId="vehicleTypeTruck" variant="filled"
                                formControlName="vehicleType" [disabled]="readOnly" />
                            <label for="vehicleTypeTruck" class="ml-2 text-sm text-gray-700">Truck</label>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <label class="mb-1 font-normal text-sm text-gray-500">Do you have vehicle insurance?</label>
                    <div class="flex flex-row items-center space-x-6 mt-1">
                        <div class="flex items-center">
                            <p-radioButton name="hasVehicleInsurance" value="yes" inputId="vehicleInsuranceYes" variant="filled"
                                formControlName="hasVehicleInsurance" [disabled]="readOnly" />
                            <label for="vehicleInsuranceYes" class="ml-2 text-sm text-gray-700">Yes</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="hasVehicleInsurance" value="no" inputId="vehicleInsuranceNo" variant="filled"
                                formControlName="hasVehicleInsurance" [disabled]="readOnly" />
                            <label for="vehicleInsuranceNo" class="ml-2 text-sm text-gray-700">No</label>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <label class="mb-1 font-normal text-sm text-gray-500">Upload Vehicle Insurance</label>
                    @if((userProfile?.onboarding?.vehicleInsurance || []).length > 0) {
                    <div class="flex flex-wrap gap-2 my-2">
                        @for (image of userProfile?.onboarding?.vehicleInsurance || []; track $index) {
                        <div class="relative">
                            <p-image [imageStyle]="{'border-radius': '8px'}"
                            [src]="image.downloadUrl"
                            alt="Image" width="150" [preview]="true" />
                            @if(!readOnly) {
                            <i class="pi pi-times absolute top-0 right-0 cursor-pointer text-gray-500 text-xs"
                                (click)="deleteImage(image, imageCategoryPaths.ShopperVehicleInsurance)"></i>
                            }
                            </div>
                            }
                            </div>
                            }
                            @if(!readOnly && (userProfile?.onboarding?.vehicleInsurance || []).length
                            <= 2) { <p-fileupload #fileupload class="flex" mode="basic" name="image" chooseIcon="pi pi-upload" [customUpload]="true"
                                [multiple]="true" accept="image/*" capture="environment" [disabled]="readOnly || uploadingImagesInProgress"
                                (uploadHandler)="uploadImage($event, fileupload, imageCategoryPaths.ShopperVehicleInsurance)" [auto]="true"
                                chooseLabel="Upload Vehicle Insurance" />
                    }
                </div>
            </div>

            <div class="flex flex-col gap-6 p-6 bg-white rounded-xl mt-4">
                <h2 class="text-md font-semibold">Grocery Shopping Experience</h2>
                <div class="flex flex-col">
                    <label class="mb-1 font-normal text-sm text-gray-500">What is your general grocery shopping
                        experience?</label>
                    <div class="flex flex-row items-center space-x-6 mt-1">
                        <div class="flex items-center">
                            <p-radioButton name="groceryShoppingExperience" value="Occasional"
                                inputId="groceryShoppingExperienceOccasional" variant="filled"
                                formControlName="groceryShoppingExperience" [disabled]="readOnly" />
                            <label for="groceryShoppingExperienceOccasional"
                                class="ml-2 text-sm text-gray-700">Occasional</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="groceryShoppingExperience" value="Regular"
                                inputId="groceryShoppingExperienceRegular" variant="filled" 
                                formControlName="groceryShoppingExperience" [disabled]="readOnly" />
                            <label for="groceryShoppingExperienceRegular"
                                class="ml-2 text-sm text-gray-700">Regular</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="groceryShoppingExperience" value="Expert"
                                inputId="groceryShoppingExperienceExpert" variant="filled"
                                formControlName="groceryShoppingExperience" [disabled]="readOnly" />
                            <label for="groceryShoppingExperienceExpert"
                                class="ml-2 text-sm text-gray-700">Expert</label>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <label class="mb-1 font-normal text-sm text-gray-500">I have Shopper Experience with other apps like
                        Instacart, Doordash, Shipt, etc.</label>
                    <div class="flex flex-row items-center space-x-6 mt-1">
                        <div class="flex items-center">
                            <p-radioButton name="hasOtherAppsExperience" value="yes" inputId="otherAppsYes"
                                variant="filled"
                                formControlName="hasOtherAppsExperience" [disabled]="readOnly" />
                            <label for="otherAppsYes" class="ml-2 text-sm text-gray-700">Yes</label>
                        </div>
                        <div class="flex items-center">
                            <p-radioButton name="hasOtherAppsExperience" value="no" inputId="otherAppsNo"
                                variant="filled"
                                formControlName="hasOtherAppsExperience" [disabled]="readOnly"  />
                            <label for="otherAppsNo" class="ml-2 text-sm text-gray-700">No</label>
                        </div>
                    </div>
                </div>
            </div>



            <div class="flex justify-center mt-4 w-full">
                <p-button styleClass="mt-4"
                    [style]="{'background': '#4D7935', 'color': '#fff', 'border-color': '#4D7935'}"
                    class="w-full [&_button]:w-full" label="Submit" (click)="submitOnboarding()" [disabled]="readOnly" />
            </div>
        </form>
    </div>
</div>